import { Accessor, Component, For, JSX, JSXElement, Match, Setter, Show, Switch } from "solid-js";
import { AttrDefinition, AttrId, AttrVal, Item, LangCode, TextValue } from "./api";
import { AttrMap, useApp } from "./context";
import { createOptions, Select } from "@thisbeyond/solid-select";
import { UserLogin } from "./login";
import { useNavigate } from "@solidjs/router";

export function Input(props: { label: string, type: "text"|"number"|"email"|"password"|"checkbox" } & JSX.InputHTMLAttributes<HTMLInputElement>) {
    return <div class="input"><label>{props.label}</label><input {...props} /></div>
}

export function I18nText({value}: {value?: TextValue}) {
    const { user } = useApp();
    return <Show when={user.lang()} keyed>
        {(lang) => {
            const val = value.translations[lang];
            if (val !== undefined) {
                return <span lang={lang}>{val}</span>
            } else {
                return <span lang="en">{value.en}</span>
            }
        }}
    </Show>
}

export function I18nTextEdit({value, setValue, label}: {value: Accessor<TextValue | null>, setValue: Setter<TextValue | null>, label?: string}) {
    function setEn(en: string) {
        const val: TextValue = value() || { en: "", translations: {} };
        val.en = en;
        if (val.en.length === 0 && Object.keys(val.translations).length === 0) {
            setValue(null);
        } else {
            setValue(val);
        }
    }
    return <div class="input">
        <label>{label}</label>
        <Input label="en" type="text" value={value()?.en} onchange={(e) => setEn(e.target.value)} />
    </div>;
}


const messages: { [key:string]: TextValue} = {
    "any": {
        "en": "any",
        translations: {
            "fr": "quelconque",
            "es": "toda",
            "de": "egal"
        }
    }
}
export function tr_text(v: TextValue, lang: LangCode): string {
    return v.translations[lang] || v.en;
}
export function tr_str(text: string, lang: LangCode): string {
    const e = messages[text];
    if (e === undefined) return text;
    return e.translations[lang] || e.en;
}

export function AttrList({attr_map, filter}: {attr_map: AttrMap, filter?: (id: AttrId, val: AttrVal) => boolean}) {
    const { cache } = useApp();
    const entries = () => {
        const out: [AttrDefinition, JSXElement][] = [];
        const attrs = cache.attrs();
        const colors = cache.colors();

        for (const [id, val] of attr_map.entries()) {
            const attr = attrs.get(id);
            if (attr === undefined) {
                continue;
            }
            const data = attr.data;

            if (filter !== undefined && !filter(id, val)) {
                continue;
            }

            let value: JSXElement | undefined;
            switch (val.type) {
                case "Length":
                    value = <span>{val.cm.toFixed(0)} cm</span>;
                    break;
                case "Volume":
                    value = <span>{val.liter.toFixed(3)} liter</span>;
                    break;
                case "Color":
                    value = <For each={val.cids}>
                        {(idx) => {
                            const c = colors.get(idx);
                            const [r, g, b] = c.rgb;
                            const s = `rgb(${r},${g},${b})`;
                            return <div><span>{c.name.en}</span><span class="colorblock" style={{"background-color": s}}></span></div>;
                            }
                        }
                    </For>;
                    break;
                case "Weight":
                    value = <span>{val.kg.toFixed(3)} kg</span>;
                    break;
                case "Number":
                    value = <span>{val.n}</span>;
                    break;
                case "Enum":
                    if (data.type === "Enum") {
                        value = <For each={val.idx}>
                            {(var_idx, idx) => <><Show when={idx() > 0}>, </Show>
                            <I18nText value={data.values[var_idx].label} /></>}
                        </For>
                    }
                    break;
            }

            out.push([attr, value]);
        }
        return out;
    };

    return <For each={entries()}>
        {([attr, elem]) => <><dt><I18nText value={attr.label} /></dt><dd>{elem}</dd></>}
    </For>
}

function CurrencySelect() {
    const { cache } = useApp();
}

export function LangSwitch() {
    const { user } = useApp();

    type Entry = { lang: LangCode, code: string, flag: string };
    const supported: LangCode[] = ["en", "de", "fr", "es", "it", "ru", "cn"];
    function flag(lang: LangCode): string {
        return [...lang.toLowerCase()].map(s => String.fromCodePoint(0x1F1E6 + s.codePointAt(0) - 97)).join("")
    }
    const countries: Entry[] = [
        { code: "gb", flag: "🇬🇧", lang: "en" },
        { code: "us", flag: "🇺🇸", lang: "en" },
        { code: "au", flag: "🇦🇺", lang: "en" },
        ... ["fr", "es", "it", "de", "ru", "cn"].map(c => ({ code: c, flag: flag(c), lang: c }))
    ];
    function onChange(c: Entry) {
        user.setLang(c.lang);
    }

    return <Select options={countries} format={({flag}) => flag}  onChange={onChange} initialValue={countries[0]} />
}

export function RequireUser({children}: { children: JSXElement }) {
    const { user } = useApp();

    return <Switch>
      <Match when={user.user() === null}>
        <h3>Login required</h3>
        <p>You need to log in or register first.</p>

        <UserLogin user={user} />
      </Match>
      <Match when={user.user() !== null} keyed>
        {(_) => children}
      </Match>
    </Switch>
}

export function OrderButton(p: {class?: string}) {
    let navigate = useNavigate();
    return <button {...p} on:click={() => navigate("/shop#order")}>Order</button>
}
